/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useLayoutEffect, Fragment } from 'react'
import intl from 'react-intl-universal'
import './index.scss'
import QrcodeLogin from './_components/qrcodeLogin'
import AccountLogin from './_components/accountLogin'
import ThirdPartyLogin from './_components/thirdPartyLogin'
import AddUserInfo from './_components/addUserInfo'
import SelectAccount from './_components/selectAccount'
import BindAccount from './_components/bindAccount'
import ForgetPwd from './_components/forgetPwd'
import dd from 'dingtalk-jsapi'
import { THIRD_PARTY } from '@/config/default'
import { useSearchParams } from 'react-router-dom'
import UpdatePsd from '../updatePsd'
import { useLoginStore } from '@/store/loginStore'

export default (function LoginForm ({ handleMfaLogin, mfaVisible }) {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('account') // account 账号登录 qrcode 扫码登录 third 三方登录
  const [agreementChecked, setAgreementChecked] = useState(() => {
    return localStorage.getItem('agreement_checked') === 'true'
  })

  const isThirdAutoLogin = searchParams.get('isThirdAutoLogin')

  // 用户同意协议后持久化到本地，下次自动勾选；取消勾选则清除
  useEffect(() => {
    if (agreementChecked) {
      localStorage.setItem('agreement_checked', 'true')
    } else {
      localStorage.removeItem('agreement_checked')
    }
  }, [agreementChecked])

  // 用于触发高度动画：账号登录子类型切换时递增
  const [accountFormVersion, setAccountFormVersion] = useState(0)
  const [showForgetPassword, setShowForgetPassword] = useState(false) // 忘记密码组件显隐
  const [loginInfo, setLoginInfo] = useState({
    updatePsdVisible: false,
    passwordLevel: 'HIGH',
    pwdValidStatus: 'HAS_BEEN_RESET',
    passwordLengthMin: 6,
    passwordLengthMax: 18,
  })
  const { showBindAccount, showSelectAccount, showAddInfo } = useLoginStore()

  const contentRef = useRef(null)
  const prevHeightRef = useRef(0)
  const agreementRef = useRef(null)

  // 切换 tab 时，内容高度过渡动画
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    // 先重置为 auto 以测量自然高度
    el.style.height = 'auto'
    const newHeight = el.scrollHeight

    const prevHeight = prevHeightRef.current || newHeight
    prevHeightRef.current = newHeight

    if (prevHeight === newHeight) return

    // 锁定到旧高度
    el.style.height = `${prevHeight}px`

    // 双 RAF 确保浏览器在旧高度完成绘制后再过渡到新高度
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.height = `${newHeight}px`
      })
    })
  }, [activeTab, accountFormVersion])

  // 过渡结束后重置为 auto，防止窗口 resize 等场景下高度异常
  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const onTransitionEnd = () => {
      el.style.height = 'auto'
    }
    el.addEventListener('transitionend', onTransitionEnd)
    return () => el.removeEventListener('transitionend', onTransitionEnd)
  }, [])

  // 检查是否第三方应用授权登录后流程
  const checkThirdPartyLogin = () => {
    // 第三方平台授权返回url后拼接state={平台名}，切换至三方登录tab，执行相应逻辑
    const thirdPartyState = searchParams.get('state')
    if (!thirdPartyState) return
    const thirdPartyList = Object.values(THIRD_PARTY)
    if (thirdPartyList.some(item => thirdPartyState.indexOf(item) !== -1)) {
      setActiveTab('third')
    }
    if (window.h5sdk || dd.env.platform !== 'notInDingTalk') {
      setActiveTab('third')
    }
  }

  // 检查是否是免输企业号自动跳转第三方授权登录流程
  const checkThirdAutoLogin = () => {
    if (isThirdAutoLogin === 'true') {
      setActiveTab('third')
    }
  }

  useEffect(() => {
    checkThirdAutoLogin()
    checkThirdPartyLogin()
  }, [])

  return (
    <div className='loginForm-container'>
      {/* 更新密码组件 */}
      {
        loginInfo.updatePsdVisible && (<UpdatePsd
          passwordLevel={loginInfo.passwordLevel}
          pwdValidStatus={loginInfo.pwdValidStatus}
          passwordLengthMin={loginInfo?.passwordLengthMin}
          passwordLengthMax={loginInfo?.passwordLengthMax}
          onCancel={() => setLoginInfo({ ...loginInfo, updatePsdVisible: false })}
        />)
      }
      {showAddInfo && <AddUserInfo />}

      {
        showForgetPassword && <ForgetPwd
          onBack={() => setShowForgetPassword(false)}
        />
      }
      {showSelectAccount && <SelectAccount />}
      {showBindAccount && <BindAccount/>}
      {
        !(showAddInfo || showForgetPassword || showSelectAccount || showBindAccount || loginInfo?.updatePsdVisible) && <Fragment>
          {/* <div className='loginForm-title'>{intl.get('login.welcomeLogin')}</div> */}
          {/* <div className='loginForm-subtitle'>{intl.get('login.selectLoginMethod')}</div> */}
          <div className='loginForm-tabs'>
            <div
              className={`loginForm-tab ${activeTab === 'qrcode' ? 'active' : ''}`}
              onClick={() => setActiveTab('qrcode')}
            >
              {intl.get('login.scanCodeLogin')}
            </div>
            <div
              className={`loginForm-tab ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              {intl.get('login.accountLoginTab')}
            </div>
            <div
              className={`loginForm-tab ${activeTab === 'third' ? 'active' : ''}`}
              onClick={() => setActiveTab('third')}
            >
              {intl.get('login.thirdPartyLoginTab')}
            </div>
          </div>
          <div className='loginForm-content' ref={contentRef}>
            {/* 扫码登录 */}
            {activeTab === 'qrcode' && <QrcodeLogin />}
            {/* 账号登录 */}
            {activeTab === 'account' && (
              <AccountLogin
                agreementRef={agreementRef}
                agreementChecked={agreementChecked}
                setAgreementChecked={setAgreementChecked}
                onAccountTypeChange={() => setAccountFormVersion(v => v + 1)}
                setShowForgetPassword={setShowForgetPassword}
                triggerUpdatePassword={setLoginInfo}
                handleMfaLogin={handleMfaLogin}
                mfaVisible={mfaVisible}
              />
            )}
            {/* 三方登录 */}
            {activeTab === 'third' && (
              <ThirdPartyLogin
                agreementRef={agreementRef}
                agreementChecked={agreementChecked}
                setAgreementChecked={setAgreementChecked}
              />
            )}
          </div>
        </Fragment>
      }
    </div>
  )
})
